import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  BookmarkIcon,
  ChevronRightIcon,
  Cog6ToothIcon,
  HomeIcon,
  PlayIcon,
  QuestionMarkCircleIcon,
  ShoppingBagIcon,
} from '@heroicons/react/24/outline';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.air-brief.com';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('atpl_access_token')}` });

export const subjectLink = (subject) => (subject.subject_type === 'game' && subject.game_identifier
  ? `/games/${subject.game_identifier.toLowerCase()}/${subject.id}`
  : `/subjects/${subject.id}`);

// The user's courses, their subjects and per-subject progress (same endpoints as the classic sidebar)
const useCourseTree = () => {
  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState({});
  const [progress, setProgress] = useState({});
  const [loading, setLoading] = useState(true);
  const [openExams, setOpenExams] = useState(0);
  const fetched = useRef(false);

  useEffect(() => {
    if (fetched.current) return undefined;
    fetched.current = true;
    const get = (path) => fetch(`${API_URL}${path}`, { headers: authHeaders() }).then((r) => (r.ok ? r.json() : null)).catch(() => null);
    const loadOpenExams = () => get('/api/exams/sessions/?status=in_progress,paused')
      .then((d) => setOpenExams((d?.results || []).length));

    get('/api/courses/user-courses/').then(async (d) => {
      const list = d?.results || [];
      setCourses(list);
      setLoading(false);
      const entries = await Promise.all(list.map((e) => get(`/api/courses/${e.course.id}/subjects/`)
        .then((s) => [e.course.id, s?.results || []])));
      setSubjects(Object.fromEntries(entries));
    });
    get('/api/courses/subjects/all-progress/').then((d) => d && setProgress(d));
    loadOpenExams();

    window.addEventListener('examSaved', loadOpenExams);
    return () => window.removeEventListener('examSaved', loadOpenExams);
  }, []);

  return { courses, subjects, progress, loading, openExams };
};

// Always-dark navy sidebar under the navbar. Desktop: full width or an icon rail (collapsed).
// Phones: a drawer (open/onClose).
const Sidebar2 = ({ collapsed, setCollapsed, open, onClose }) => {
  const { pathname } = useLocation();
  const { courses, subjects, progress, loading, openExams } = useCourseTree();
  const [openCourse, setOpenCourse] = useState(null);

  // The drawer always shows labels; the desktop rail shows icons only
  const rail = collapsed && !open;

  useEffect(() => {
    if (courses.length && openCourse === null) {
      // Open the course of the subject being viewed, else the first course
      const current = courses.find((e) => (subjects[e.course.id] || []).some((s) => pathname === subjectLink(s)));
      setOpenCourse((current || courses[0]).course.id);
    }
  }, [courses, subjects, pathname, openCourse]);

  const navItem = 'group flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors';
  const navState = (active) => (active ? 'bg-white/10 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white');
  const nav = [
    { label: 'Dashboard', to: '/dashboard', icon: HomeIcon },
    { label: 'Store', to: '/store', icon: ShoppingBagIcon },
    { label: 'My tests', to: '/tests', icon: BookmarkIcon, badge: openExams },
  ];
  const footer = [
    { label: 'Support', to: '/support', icon: QuestionMarkCircleIcon },
    { label: 'Settings', to: '/settings', icon: Cog6ToothIcon },
  ];

  const renderItem = (item) => {
    const active = item.to && pathname === item.to;
    const body = (
      <>
        <span className="relative">
          <item.icon className="h-5 w-5 flex-shrink-0" />
          {rail && item.badge > 0 && <span className="absolute -right-1.5 -top-1.5 h-2.5 w-2.5 rounded-full bg-rose-500 ring-2 ring-slate-950" />}
        </span>
        {!rail && <span className="flex-1 text-left">{item.label}</span>}
        {!rail && item.badge > 0 && <span className="rounded-full bg-rose-500/90 px-1.5 text-[11px] font-semibold text-white">{item.badge}</span>}
      </>
    );
    const cls = `${navItem} ${navState(active)} ${rail ? 'justify-center px-0' : ''}`;
    return item.to
      ? <Link key={item.label} to={item.to} onClick={onClose} className={cls} title={rail ? item.label : undefined} aria-current={active ? 'page' : undefined}>{body}</Link>
      : <button key={item.label} onClick={item.onClick} className={cls} title={rail ? item.label : undefined}>{body}</button>;
  };

  return (
    <>
      <aside
        className={`fixed bottom-0 left-0 top-16 z-40 flex w-72 flex-col overflow-hidden border-r border-white/10 bg-slate-950 text-gray-300 transition-all duration-300 ${
          rail ? 'md:w-[72px]' : 'md:w-64'} ${open ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}`}
        aria-label="Sidebar"
      >
        <div className="pointer-events-none absolute -left-20 -top-24 h-56 w-56 rounded-full bg-primary-600/15 blur-3xl" />

        <nav className="relative flex-shrink-0 space-y-0.5 px-3 pt-4">{nav.map(renderItem)}</nav>

        {/* Courses and subjects */}
        <div className="relative mt-6 flex min-h-0 flex-1 flex-col">
          {!rail && <p className="px-6 pb-2 text-[11px] font-semibold uppercase tracking-widest text-primary-400">My courses</p>}
          <div className="flex-1 space-y-1 overflow-y-auto px-3 pb-4 [scrollbar-color:rgba(255,255,255,0.15)_transparent] [scrollbar-width:thin]">
            {loading && !rail && <p className="px-3 py-2 text-xs text-gray-500">Loading courses...</p>}
            {!loading && courses.length === 0 && !rail && (
              <div className="mx-1 rounded-xl border border-dashed border-white/15 p-4">
                <p className="text-sm font-medium text-white">No courses yet</p>
                <p className="mt-1 text-xs text-gray-400">Pick a plan to unlock your subjects.</p>
                <Link to="/store" onClick={onClose} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-primary-300 hover:text-primary-200">
                  Browse plans <ChevronRightIcon className="h-3.5 w-3.5" />
                </Link>
              </div>
            )}
            {courses.map(({ id, course }) => {
              const isOpen = openCourse === course.id && !rail;
              const list = subjects[course.id] || [];
              return (
                <div key={id}>
                  <button
                    onClick={() => {
                      if (rail) { setCollapsed(false); setOpenCourse(course.id); return; }
                      setOpenCourse(isOpen ? -1 : course.id);
                    }}
                    title={rail ? course.name : undefined}
                    className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors hover:bg-white/5 ${rail ? 'justify-center' : ''}`}
                  >
                    <span className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-primary-600/15 text-[11px] font-bold tracking-wide text-primary-300">
                      {(course.code || course.name).slice(0, 4)}
                    </span>
                    {!rail && (
                      <>
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium text-white">{course.name}</span>
                          <span className="block text-xs text-gray-500">{list.length || course.subject_count || 0} subjects</span>
                        </span>
                        <ChevronRightIcon className={`h-4 w-4 flex-shrink-0 text-gray-500 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                      </>
                    )}
                  </button>
                  {isOpen && (
                    <ul className="mb-2 ml-[1.35rem] mt-1 space-y-0.5 border-l border-white/10 pl-3">
                      {list.map((s) => {
                        const to = subjectLink(s);
                        const active = pathname === to;
                        const pct = Math.round(progress[s.id]?.completion_rate || 0);
                        const isGame = s.subject_type === 'game';
                        return (
                          <li key={s.id}>
                            <Link
                              to={to}
                              onClick={onClose}
                              className={`block rounded-lg px-2.5 py-1.5 transition-colors ${active ? 'bg-primary-600/20 text-white' : 'text-gray-400 hover:bg-white/5 hover:text-white'}`}
                            >
                              <span className="flex items-center gap-2 text-[13px]">
                                <span className="w-9 flex-shrink-0 text-[11px] font-semibold text-gray-500">{s.code}</span>
                                <span className="min-w-0 flex-1 truncate">{s.name}</span>
                                {isGame
                                  ? <PlayIcon className="h-3.5 w-3.5 flex-shrink-0 text-primary-300" />
                                  : pct > 0 && <span className="text-[11px] tabular-nums text-gray-500">{pct}%</span>}
                              </span>
                              {!isGame && pct > 0 && (
                                <span className="ml-11 mt-1 block h-0.5 rounded-full bg-white/10">
                                  <span className="block h-full rounded-full bg-primary-500" style={{ width: `${Math.min(100, pct)}%` }} />
                                </span>
                              )}
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        <nav className="relative flex-shrink-0 space-y-0.5 border-t border-white/10 p-3">{footer.map(renderItem)}</nav>
      </aside>

    </>
  );
};

export default Sidebar2;
